import {
	isStreamAnswerPart,
	isStreamResearchStepCompleted,
	isStreamResearchStepTitle,
	isStreamResearchStepTitleShort,
	isStreamResponse,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import type {
	ParsedSSEMessage,
	StreamMessage,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import type {
	Conversation,
	StoreChatItem,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';

import type { ChatStoreContainerProps } from './types';

type UpdateCurrentConversation = (data: Partial<Conversation>) => void;

export interface ReplayFlushOptions {
	conversationId: string;

	/**
	 * Captured just BEFORE the reconnect API call is made. SSE event IDs are
	 * server-side Unix timestamps (with an optional sequence suffix, e.g.
	 * "1775850341989-0"). Any event whose numeric prefix is < reconnectTimestamp
	 * was emitted before this reconnect began and is therefore a replay.
	 */
	reconnectTimestamp: number;

	getMessages: ChatStoreContainerProps['getMessages'];
	setMessages: ChatStoreContainerProps['setMessages'];
	processStreamResearch: ChatStoreContainerProps['processStreamResearch'];
	updateCurrentConversation: UpdateCurrentConversation;

	/** Hook called at flush time, after store updates commit. Drain caller-side buffers here. */
	onFlush?: () => void;
}

type ProcessStreamResearchParams = Parameters<ChatStoreContainerProps['processStreamResearch']>[0];

export interface ReplayFlushResult {
	/**
	 * Wrap the source generator (from createReconnectableStream) with this before
	 * passing to processReconnectStreamMessage. Flushes all buffered store updates
	 * just before the first live chunk is yielded.
	 */
	wrapGenerator: (
		source: AsyncGenerator<ParsedSSEMessage<StreamMessage>>,
	) => AsyncGenerator<ParsedSSEMessage<StreamMessage>>;

	/** Pass instead of the real setMessages — writes are buffered during replay and committed as a single update on flush. */
	batchedSetMessages: ChatStoreContainerProps['setMessages'];

	/** Pass instead of the real getMessages — reads from the in-memory buffer during replay so read-then-write patterns stay coherent. */
	batchedGetMessages: ChatStoreContainerProps['getMessages'];

	/**
	 * Pass instead of the real updateCurrentConversation — patches are merged
	 * during replay (later keys win) and committed as a single update on flush.
	 * Pre-seeded with { traces: [] } so "Resuming response..." is always cleared.
	 */
	batchedUpdateCurrentConversation: UpdateCurrentConversation;

	/**
	 * Pass instead of the real processStreamResearch — calls are queued during
	 * replay and replayed synchronously on flush so the research store jumps
	 * directly to its final state in one render, skipping intermediate spinners.
	 */
	batchedProcessStreamResearch: ChatStoreContainerProps['processStreamResearch'];

	/**
	 * Returns true while replay chunks are being processed. Consumers with
	 * artificial delays (e.g. research step progress timers) can use this to
	 * skip them during replay.
	 */
	getIsReplaying: () => boolean;

	/** stepIds that received STEP_COMPLETED during replay — used by flushFiltered on the reasoning buffer. */
	getCompletedStepIds: () => ReadonlySet<string>;
}

/**
 * Wraps a reconnect stream generator to batch store updates during the "replay"
 * phase into a single flush just before the first live chunk is processed.
 *
 * Pipeline:
 *   createReconnectableStream  (network / reconnection)
 *     → withReplayFlush        (replay batching)
 *       → processReconnectStreamMessage  (store updates)
 *
 * Replay detection: SSE event IDs are server-side Unix timestamps with an
 * optional sequence suffix (e.g. "1775850341989-0"). We parse the numeric
 * prefix and compare to reconnectTimestamp. Chunks with no id or a non-numeric
 * id are treated as replay until a live chunk is seen.
 */
export function withReplayFlush({
	conversationId,
	reconnectTimestamp,
	getMessages,
	setMessages,
	processStreamResearch,
	updateCurrentConversation,
	onFlush,
}: ReplayFlushOptions): ReplayFlushResult {
	// Lazily initialised on first setMessages write — avoids copying the full
	// messages array upfront when multiple tabs reconnect simultaneously.
	// replayBuffering=true + replayBuffer=null → buffering, no write yet
	// replayBuffering=false + replayBuffer=null → flushed
	let replayBuffer: StoreChatItem[] | null = null;
	let replayBuffering = true;

	// Pre-seeded with { traces: [] } so flush always clears "Resuming response..."
	// even if no clearTracesIfNotEmpty call fires during replay.
	// null means flushed.
	let conversationPatch: Parameters<UpdateCurrentConversation>[0] | null = { traces: [] };

	// null means flushed.
	let researchQueue: ProcessStreamResearchParams[] | null = [];

	// Per-step tracking for reconnect replay. Keyed by stepId.
	// stepTitleEvents: first STEP_TITLE seen (provides title + roundId).
	// stepCompletedEvents: last STEP_COMPLETED seen (marks step done).
	// Used by getCollapsedStepEvents() and getCompletedStepIds().
	const stepTitleEvents = new Map<string, ProcessStreamResearchParams>();
	const stepCompletedEvents = new Map<string, ProcessStreamResearchParams>();

	const trackStepEvent = (params: ProcessStreamResearchParams): void => {
		const msg = params.streamMessage;
		if (isStreamResearchStepTitle(msg) || isStreamResearchStepTitleShort(msg)) {
			if (!stepTitleEvents.has(msg.message.stepId)) {
				stepTitleEvents.set(msg.message.stepId, params);
			}
		} else if (isStreamResearchStepCompleted(msg)) {
			stepCompletedEvents.set(msg.message.stepId, params);
		}
	};

	const getCollapsedStepEvents = (): ProcessStreamResearchParams[] => {
		const result: ProcessStreamResearchParams[] = [];
		const allStepIds = new Set([
			...Array.from(stepTitleEvents.keys()),
			...Array.from(stepCompletedEvents.keys()),
		]);
		allStepIds.forEach((stepId) => {
			const titleEvent = stepTitleEvents.get(stepId);
			const completedEvent = stepCompletedEvents.get(stepId);
			if (titleEvent) result.push(titleEvent);
			if (completedEvent) result.push(completedEvent);
		});
		return result;
	};

	const isBuffering = () => replayBuffering;

	const parseEventTime = (id: string): number => Number(id.split('-')[0]);

	const flush = () => {
		if (!isBuffering()) {
			return;
		}

		const buffered = replayBuffer;
		const patch = conversationPatch;
		const queued = researchQueue!;

		// Mark flushed before dispatching so re-entrant calls go live.
		replayBuffering = false;
		replayBuffer = null;
		conversationPatch = null;
		researchQueue = null;

		if (buffered !== null) {
			setMessages(conversationId, buffered);
		}

		if (patch !== null) {
			updateCurrentConversation(patch);
		}

		// onFlush runs BEFORE the queued research dispatch so caller-side
		// buffers (e.g. reasoning chunks) populate step content first; the
		// collapse signals (ANSWER_PART, FINAL_RESPONSE) then fire on
		// already-populated steps, matching the source event order.
		onFlush?.();

		// react-sweet-state setState is synchronous — the research store reaches
		// its final state before React re-renders, giving one render with
		// researchCompleted=true and skipping intermediate spinner states.
		queued.forEach((params) => processStreamResearch(params));
	};

	const batchedSetMessages: ChatStoreContainerProps['setMessages'] = (convId, msgs) => {
		if (replayBuffering && convId === conversationId) {
			replayBuffer = msgs;
			return;
		}
		setMessages(convId, msgs);
	};

	const batchedGetMessages: ChatStoreContainerProps['getMessages'] = (convId) => {
		if (replayBuffering && convId === conversationId) {
			// Fall through to real store if no write has arrived yet.
			return replayBuffer ?? getMessages(convId);
		}
		return getMessages(convId);
	};

	const batchedUpdateCurrentConversation: UpdateCurrentConversation = (patch) => {
		if (conversationPatch !== null) {
			conversationPatch = { ...conversationPatch, ...patch };
			return;
		}
		updateCurrentConversation(patch);
	};

	const batchedProcessStreamResearch: ChatStoreContainerProps['processStreamResearch'] = (
		params,
	) => {
		if (researchQueue !== null) {
			if (isStreamResponse(params.streamMessage)) {
				// Preserve ANSWER_PART if already queued so both hasReceivedAnswerPart
				// and researchCompleted are set at flush.
				const hasAnswerPart = researchQueue.some((p) => isStreamAnswerPart(p.streamMessage));
				researchQueue = hasAnswerPart ? [...researchQueue, params] : [params];
				return;
			}

			if (isStreamAnswerPart(params.streamMessage)) {
				// Collapse to [STEP_TITLE, STEP_COMPLETED per step, ANSWER_PART] so
				// past rounds have steps in the store after flush — prevents "Rovo is
				// thinking" for completed rounds during mid-stream reload.
				researchQueue = [...getCollapsedStepEvents(), params];
				return;
			}

			trackStepEvent(params);

			researchQueue.push(params);
			return;
		}
		processStreamResearch(params);
	};

	async function* replayFlushGenerator(
		source: AsyncGenerator<ParsedSSEMessage<StreamMessage>>,
	): AsyncGenerator<ParsedSSEMessage<StreamMessage>> {
		let replayEventCount = 0;

		for await (const chunk of source) {
			if (isBuffering() && chunk.id) {
				const eventTime = parseEventTime(chunk.id);
				if (!isNaN(eventTime) && eventTime >= reconnectTimestamp) {
					flush();
				}
			}

			// During replay, yield the event loop periodically to prevent the tab
			// from becoming unresponsive when processing large streams (e.g. 6K+ events).
			// This gives the browser a chance to handle user interactions and GC between batches.
			if (isBuffering()) {
				replayEventCount++;
				if (replayEventCount % 200 === 0) {
					await new Promise<void>((resolve) => setTimeout(resolve, 0));
				}
			}

			yield chunk;
		}
		// Stream ended while still in replay (response was complete on reconnect).
		flush();
	}

	return {
		wrapGenerator: replayFlushGenerator,
		batchedSetMessages,
		batchedGetMessages,
		batchedProcessStreamResearch,
		batchedUpdateCurrentConversation,
		getIsReplaying: isBuffering,
		getCompletedStepIds: () => new Set(stepCompletedEvents.keys()),
	};
}
